import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { LuArrowLeft, LuShieldAlert, LuClock, LuExternalLink, LuRefreshCw, LuTriangleAlert, LuDatabase, LuBot } from "react-icons/lu";
import { API } from "../App";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const AdminLogsPage = ({ auth }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (auth.user?.role !== "admin") return;
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API}/admin/logs`, {
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            if (response.ok) {
                setLogs(await response.json());
            } else {
                toast.error("Error al cargar logs");
            }
        } catch (error) {
            toast.error("Error de conexión");
        } finally {
            setLoading(false);
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'whatsapp': return <LuBot className="w-4 h-4 text-green-500" />;
            case 'api': return <LuDatabase className="w-4 h-4 text-blue-500" />;
            default: return <LuTriangleAlert className="w-4 h-4 text-amber-500" />;
        }
    };

    if (auth.user?.role !== "admin") {
        return (
            <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
                <LuShieldAlert className="w-16 h-16 text-destructive mb-4" />
                <h1 className="text-2xl font-bold text-foreground mb-2">Acceso Denegado</h1>
                <p className="text-muted-foreground mb-6">Solo los administradores pueden ver los registros del sistema.</p>
                <Link to="/dashboard">
                    <Button>Volver al Dashboard</Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <header className="sticky top-0 z-50 glass border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-4 min-w-0">
                        <Link to="/dashboard">
                            <Button variant="ghost" size="icon" className="rounded-xl shrink-0">
                                <LuArrowLeft className="w-5 h-5" />
                            </Button>
                        </Link>
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center shrink-0">
                                <LuShieldAlert className="w-5 h-5 text-destructive" />
                            </div>
                            <div className="min-w-0">
                                <h1 className="font-heading font-bold text-lg text-foreground truncate">Logs de Errores</h1>
                                <p className="text-xs text-muted-foreground truncate">Monitoreo del sistema en tiempo real</p>
                            </div>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchLogs} className="rounded-xl gap-2 self-start sm:self-auto">
                        <LuRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Actualizar
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
                {logs.length === 0 && !loading ? (
                    <Card className="glass-card border-white/10 p-12 text-center">
                        <LuTriangleAlert className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                        <h3 className="text-lg font-medium text-muted-foreground">No hay errores registrados</h3>
                        <p className="text-sm text-muted-foreground/60">¡Todo parece funcionar correctamente!</p>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {logs.map((log, index) => (
                            <motion.div
                                key={log.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <Card className="glass-card border-white/5 hover:border-white/10 transition-all">
                                    <div className="p-4 flex flex-col md:flex-row md:items-center gap-4">
                                        <div className="flex items-center gap-3 min-w-[150px]">
                                            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                                                {getTypeIcon(log.type)}
                                            </div>
                                            <Badge variant="outline" className="capitalize text-[10px]">
                                                {log.type}
                                            </Badge>
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-foreground">{log.message}</p>
                                            {log.details && (
                                                <pre className="mt-2 p-2 bg-black/20 rounded text-[10px] text-muted-foreground overflow-x-auto font-mono">
                                                    {JSON.stringify(log.details, null, 2)}
                                                </pre>
                                            )}
                                        </div>

                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                                <LuClock className="w-3 h-3" />
                                                {format(new Date(log.timestamp), "d MMM, HH:mm:ss", { locale: es })}
                                            </div>
                                            {log.agent_id && (
                                                <Badge variant="secondary" className="text-[9px] py-0">
                                                    Agent: {log.agent_id.substring(0, 8)}...
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminLogsPage;
